import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HolderOutlined } from "@ant-design/icons";

/**
 * El `<th>` de una columna arrastrable, tomando como referencia la tabla de productos de
 * Presupuestos (`SellerBlock.tsx`, xcons-docs#366 punto 9). Ahí el drag de columnas convive
 * con el de filas y usa `@dnd-kit/core` a mano; acá no hay filas que reordenar, así que
 * alcanza con `@dnd-kit/sortable` sobre las columnas solas.
 *
 * `columnKey` llega vía `onHeaderCell` de cada columna, no del `key` de React: AntD no lo
 * expone como prop.
 *
 * `handleInline` pone el asa y el título en una misma fila, alineados al centro. Hace falta
 * cuando el título es un bloque —el sorter de antd con un `<span display:block>` adentro,
 * como en Presupuestos—, que si no empuja el asa a una línea propia arriba del label.
 */
export const CatalogColumnHeaderCell: React.FC<
  React.HTMLAttributes<HTMLTableCellElement> & {
    columnKey?: string;
    handleInline?: boolean;
  }
> = ({ columnKey, handleInline, children, style, ...restProps }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnKey ?? "" });

  if (!columnKey) {
    return (
      <th style={style} {...restProps}>
        {children}
      </th>
    );
  }

  const handle = (
    <span
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      style={{
        // El sorter de antd cubre todo el `<th>` con un `::after` absoluto; sin esto el
        // asa queda debajo y el arrastre nunca arranca.
        position: "relative",
        zIndex: 1,
        cursor: "grab",
        display: "inline-flex",
        alignItems: "center",
        marginRight: 4,
        touchAction: "none",
        color: "rgba(0,0,0,0.3)",
      }}
    >
      <HolderOutlined />
    </span>
  );

  return (
    <th
      ref={setNodeRef}
      style={{
        ...style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging
          ? { position: "relative", zIndex: 9, background: "#fafafa" }
          : {}),
      }}
      {...restProps}
    >
      {handleInline ? (
        <div style={{ display: "flex", alignItems: "center" }}>
          {handle}
          <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
      ) : (
        <>
          {handle}
          {children}
        </>
      )}
    </th>
  );
};
